import axios, { AxiosResponse } from "axios";
import config from "./config";
import { badPathReason, guarded, joinUrl, pathSegment } from "../utils/url";
import { PickupAddressFormProps } from "../components/ui/PickupAddressDrawer";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClientAddressType } from "../hooks/usePickupAddress";
// import { ReturnsFormType } from "../components/features/returns/AddReturns";
import { Dayjs } from "dayjs";

const options = {
  timeout: 100000,
  headers: {
    "Content-Type": "application/json;charset=utf-8",
  },
};
// API server root with no trailing slash, whatever config.APIDEV is set to
export const API_BASE = String(config.APIDEV).replace(/\/+$/, "");

// Full URL on the API server, for calls that use fetch/axios/window.open directly instead of `api`.
// apiUrl("/b2c/service_list/") -> "http://127.0.0.1:8000/b2c/service_list/"
export const apiUrl = (path: string) => joinUrl(API_BASE, path);

export const api = axios.create({
  baseURL: API_BASE,
  ...options,
});

api.interceptors.request.use(
  (config) => {
    // Never send a path with "//" after the host (usually an empty id) - fail loudly instead
    const bad = badPathReason(config.url ?? "");
    if (bad) return Promise.reject(new Error(bad));
    const token = localStorage.getItem("hipship-token");
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    const errorCode = error?.response?.status;

    if (errorCode === 401) {
      localStorage.clear();
    } else if (errorCode === 500) {
      toast.error("Something went wrong!");
    }
    return Promise.reject(error);
  }
);

interface loginType {
  username: string;
  password: string;
}

// user login
export const userLogin = (loginData: loginType) => {
  return api.post("/bidsite/login/", loginData);
};

interface registerType {
  user: object;
  mobile: string;
  address: string;
  // email: string;
  // password: string;
  user_type: string;
  city: string;
  state: string;
  pincode: string;
  company_name: string;
  pan_number: string;
  business_type: string;
  shipment_volume: string;
}

//user register
export const UserRegister = (loginData: registerType) => {
  return api.post("/bidsite/register/", loginData);
};

// 680694
export const GetStateByPincode = async (code: string) => {
  return api.get("/b2c/getstate/?pincode=" + code);
};

export const getUserPickupAddress = () => {
  return api.get("/b2c/pickup_address/");
};

export const getPincodeApi = (pincode: string) => {
  return api.get("/b2c/getstate/", {
    params: {
      pincode: pincode,
    },
  });
};

export const resetPassword = (data: {
  email: string;
  otp: string;
  new_password: string;
}) => {
  return api.post(
    `/bidsite/reset_password_otp/?email=${data.email}&otp=${data.otp}&new_password=${data.new_password}`
  );
};
export const forgotPassword = (data: { email: string }) => {
  return api.post(`/bidsite/forgot_password_otp/?email=${data.email}`);
};

export const storePickupAddress = (data: PickupAddressFormProps) => {
  return api.post("b2c/pickup_address/", data);
};

export const disputeShipment = (
  status: string,
  from_date: string,
  to_date: string
) => {
  return api.get("/b2c/dispute_shipment/", {
    params: {
      status,
      from_date,
      to_date,
    },
  });
};

export const disputeShipmentPost = (dispute_id: any, status: string) => {
  return api.post(
    `/b2c/dispute_shipment/?dispute_id=${dispute_id}&status=${status}`
  );
};

export function useGetPickupAddress() {
  const { isLoading, data, isError, error } = useQuery({
    queryKey: ["pickup-address"],
    queryFn: getUserPickupAddress,
  });

  let pickupAddress: ClientAddressType[] = [];

  if (!isLoading && !isError && data) {
    pickupAddress = data?.data as ClientAddressType[];
  }

  return { isLoading, error, pickupAddress };
}

export const deletePickupAddress = (id: number) => {
  return api.delete("/b2c/pickup_address/", {
    params: { id },
  });
};

export const useDeletePickupAddress = () => {
  const queryClient = useQueryClient();

  const { isPending: isDeleting, mutate: deleteAddress } = useMutation({
    mutationFn: (id: number) => deletePickupAddress(id),
    onSuccess: () => {
      toast.success("Pickup address deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["pickup-address"],
      });
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message || "Failed to delete pickup address"
      );
    },
  });

  return { isDeleting, deleteAddress };
};

export const useAddPickupAddress = () => {
  const queryClient = useQueryClient();

  const { isSuccess, mutate: storeAddress } = useMutation({
    mutationFn: (data: PickupAddressFormProps) => storePickupAddress(data),
    onSuccess: () => {
      toast.success("Pickup address added successfully");

      queryClient.invalidateQueries({
        queryKey: ["pickup-address"],
      });
    },
    onError: (err) => toast.error(err.message),
  });

  return { isSuccess, storeAddress };
};

interface companyTypes {
  company_name: string;
  company_url: string;
  company_email: string;
  company_logo_image: File | undefined;
}

export const addCompanyDetail = (data: companyTypes) => {
  return api.post("/bidsite/add_company_profile/", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const fetchCompanyDetail = () => {
  return api.get("/bidsite/add_company_profile/");
};

export const updateCompanyDetail = (data: companyTypes) => {
  return api.put("/bidsite/add_company_profile/", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Without opts.page_size: the whole report (used for the Excel download). With it: one page
// (newest first) as {headers, excel_data, count, page, page_size, num_pages}, optional search.
export const FetchReports = (
  startDate: Dayjs,
  endDate: Dayjs,
  reportType: string,
  opts?: ListPageOpts
) => {
  const params: Record<string, string | number> = {
    from_date: startDate.format("YYYY-MM-DD"),
    to_date: endDate.format("YYYY-MM-DD"),
    report_type: reportType,
  };
  if (opts?.page_size) {
    params.page = opts.page ?? 1;
    params.page_size = opts.page_size;
    if (opts.search?.trim()) params.search = opts.search.trim();
  }
  return api.get(`/b2c/order_reports/`, { params });
};

// Cancel / delete many orders at once. Single-order cancellation uses /b2c/cancel_order/.
export const cancelBulkOrders = (orderIds: string[]) => {
  return api.post("/b2c/cancel_bulk_order/", orderIds);
};


// Without opts.page_size: the whole report (used for the Excel download). With it: one page
// (newest first) as {headers, excel_data, count, page, page_size, num_pages}, optional search.
export const FetchMISReports = (
  startDate: Dayjs,
  endDate: Dayjs,
  reportType: string,
  opts?: ListPageOpts
) => {
  const params: Record<string, string | number> = {
    from_date: startDate.format("YYYY-MM-DD"),
    to_date: endDate.format("YYYY-MM-DD"),
    report_type: reportType,
  };
  if (opts?.page_size) {
    params.page = opts.page ?? 1;
    params.page_size = opts.page_size;
    if (opts.search?.trim()) params.search = opts.search.trim();
  }
  return api.get(`/b2c/mis_reports/`, { params });
};

interface PrintType {
  doc_type: string;
  awb_numbers?: any;
  shipment_ids?: any;
}

export const BulkPrintActionApi = (data: PrintType) => {
  return api.post("/b2c/bulk_print/", data, {
    responseType: "blob",
  });
};
// Billing lists. Without `opts` (or without opts.page_size) each call returns exactly what it
// always did -- the whole period -- which the Excel export relies on. With opts.page_size the
// server returns one page (newest first) plus count / page / page_size / num_pages; `search`
// filters on the server and `totals: false` skips the summary totals (cheaper next pages).
export interface ListPageOpts {
  page?: number; // 1-based
  page_size?: number;
  search?: string;
  totals?: boolean;
}

const billingParams = (startDate: Dayjs, endDate: Dayjs, page: number | undefined, opts?: ListPageOpts) => {
  const params: Record<string, string | number> = {
    from_date: startDate.format("YYYY-MM-DD"),
    to_date: endDate.format("YYYY-MM-DD"),
  };
  if (opts?.page_size) {
    params.page = opts.page ?? 1;
    params.page_size = opts.page_size;
    if (opts.search?.trim()) params.search = opts.search.trim();
    if (opts.totals === false) params.totals = 0;
  } else if (page !== undefined) {
    params.page = page;
  }
  return params;
};

export const getAllShipmentData = (page: number, startDate: Dayjs, endDate: Dayjs, opts?: ListPageOpts) =>
  api.get(`/b2c/billing/shipment_charges/`, { params: billingParams(startDate, endDate, page, opts) });

export const getAllCodRemittanceData = (
  page: number,
  startDate: Dayjs,
  endDate: Dayjs,
  cod_type: string,
  opts?: ListPageOpts
) => api.get(`/b2c/billing/cod_remittance/`, { params: { ...billingParams(startDate, endDate, page, opts), cod_type } });

export const getAllWalletHistoryData = (startDate: Dayjs, endDate: Dayjs, opts?: ListPageOpts) =>
  api.get(`/payment/walletHistoryApiView/`, { params: billingParams(startDate, endDate, undefined, opts) });

export const getAllInvoiceData = (page: number, startDate: Dayjs, endDate: Dayjs, opts?: ListPageOpts) =>
  api.get(`/b2c/billing/invoice/`, { params: billingParams(startDate, endDate, page, opts) });

export const getAllPassbookData = (page: number, startDate: Dayjs, endDate: Dayjs, opts?: ListPageOpts) =>
  api.get(`/b2c/billing/passbook/`, { params: billingParams(startDate, endDate, page, opts) });

interface RateFormData {
  shipment_type: string;
  pickup_pincode: string;
  desti_pincode: string;
  invoice_value: string;
  cod: string;
  cod_amount: string;
  shipment_weight: string;
  shipment_length: string;
  shipment_width: string;
  shipment_height: string;
  is_return: boolean;
}

// user login
// export const getRateCalculator = (RateFormData: RateFormData) => {
//   return api.post("/b2b/rate_calculator/", RateFormData);
// };

export const getRateCalculator = (data: RateFormData) => {
  return api.post("/b2c/rate_calculator/", data);
};

// user roles apis
export const fetchAllUserRole = () => {
  return api.get("/bidsite/StaffApiView/");
};

export const fetchAllRoles = () => {
  return api.get("/bidsite/clientMenus/");
};

interface RoleFormData {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  user_type: string;
  modules: string[];
}

export const postUserRole = (data: RoleFormData) => {
  return api.post("/bidsite/StaffApiView/", data, {});
};

export const fetchSingleRoles = (user_id: any) => {
  return guarded(() => api.get(`/bidsite/DetailsStaffApiView/${pathSegment(user_id, "user id")}`));
};

export const putSingleRoles = (user_id: any, data: any) => {
  return guarded(() => api.put(`/bidsite/DetailsStaffApiView/${pathSegment(user_id, "user id")}`, data));
};

export const fetchAwbno = () => {
  return api.get(`/bidsite/fetch_awb_no/`);
};

export const fetchTicketCategory = () => {
  return api.get(`/bidsite/ticket_categorys/`);
};
interface AwbnoData {
  id: string;
  guid: string;
}

export interface TicketFormData {
  awb_no: string[];
  category: string;
  query_remark: string;
}

export const createTicket = (data: TicketFormData) => {
  return api.post("/bidsite/ticket/", data, {});
};

export const getAllTicket = () => {
  return api.get("/bidsite/ticket/");
};

export interface CloseTicketData {
  query_id: string;
  status: string;
}

export const CloseTicketByAdmin = (data: CloseTicketData) => {
  return api.put("/bidsite/ticket_update/", data, {});
};

export const getMenuSubMenu = () => {
  return api.get("/bidsite/sideMenus/");
};

// Buyer tickets raised on this client's own shipments (via the public
// tracking-page chatbot). Server-side filtered to only this logged-in
// client's shipments — one client can never see another client's tickets.
export const getMyBuyerTickets = () => {
  return api.get("/b2c/my_tickets/");
};

export interface TicketMessageType {
  id: number;
  sender_type: "admin" | "client";
  sender_name: string;
  message: string;
  created_at: string;
}

// Chat thread for one of this client's own tickets. The backend re-checks
// ownership on every call - a client can never fetch or post into another
// client's ticket, even if they somehow guessed the ticket_id.
export const getMyTicketMessages = (ticket_id: string) => {
  return guarded(() => api.get(`/b2c/my_tickets/${pathSegment(ticket_id, "ticket id")}/messages/`));
};

export const sendMyTicketMessage = (ticket_id: string, message: string) => {
  return guarded(() => api.post(`/b2c/my_tickets/${pathSegment(ticket_id, "ticket id")}/messages/`, { message }));
};

export interface PermissionData {
  staff_id: string;
}

export const getSubUserRoles = (data: PermissionData) => {
  return api.post("/bidsite/check_user_roles/", data, {});
};